import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { InformationSidePanelModalService } from 'gain-lib/information-side-panel-modal/information-side-panel-modal.service';
import {
  LocationFormHelper,
  RegionOptionGroup,
} from 'gain-web/shared-modules/geography/location-form-helper.service';
import { ApiClient } from 'gain-web/shared-services/api-client.generated.service';
import { Observable, Subject, of } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-location-form',
  templateUrl: './location-form.component.html',
  styleUrls: ['./location-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LocationFormComponent implements OnInit, OnDestroy {
  onDestroy$ = new Subject();

  // Prevent enter press to prevent textarea from adding empty rows
  @HostListener('keypress', ['$event'])
  onKeyPress(keyPress: KeyboardEvent) {
    if (keyPress.key === 'Enter') {
      keyPress.preventDefault();
    }
  }

  @Input()
  countryLabel: string = 'Country';

  @Input()
  regionLabel: string = 'Region';

  @Input()
  countryControl!: FormControl<string | null>;

  @Input()
  regionControl!: FormControl<string | null>;

  @Input()
  disabledCountries?: string[];

  // Hides generic/placeholder countries (e.g. XX) from this field's options (US 7568852).
  @Input()
  excludeGenericCountries = false;

  @Input()
  displayCountryInfo: boolean = false;

  @Input()
  displaySocialTaxInfo: boolean = false;

  @Input()
  countryRequired: boolean = false;

  @Input()
  regionRequired: boolean = false;

  @Input()
  countryRegionValidation = true;

  @Input()
  showValidationErrors = true;

  @Input()
  isExcludeSanctionedCountry: boolean = false;

  filteredCountries!: Observable<ApiClient.ICountryDto[]>;

  filteredRegions!: Observable<ApiClient.RegionDto[]>;
  filteredRegionGroups!: Observable<RegionOptionGroup[]>;

  @Output()
  optionSelected = new EventEmitter<void>();

  constructor(
    public locationFormHelper: LocationFormHelper,
    private _infoPanel: InformationSidePanelModalService,
  ) {}

  ngOnInit(): void {
    this.locationFormHelper.init({
      countryControl: this.countryControl,
      regionControl: this.regionControl,
      onDestroy$: this.onDestroy$,
      countryRegionValidation: this.countryRegionValidation,
    });

    this.filteredCountries = this._getCountryOptions();

    if (this.countryRegionValidation) {
      this.filteredRegions = this.locationFormHelper.getRegionOptions(
        this.countryControl,
      );
    } else {
      this.filteredRegionGroups = this.locationFormHelper.getAllRegionGroups();
    }

    this.countryControl.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.optionSelected.emit();

        // Do not refresh or filter all regions in independent mode.
        if (this.countryRegionValidation) {
          this.filteredRegions = this.locationFormHelper.getRegionOptions(
            this.countryControl,
          );
        }
      });
  }

  onSearchRegion(searchTerm: string): void {
    if (this.countryRegionValidation) {
      this.filteredRegions = this.locationFormHelper.getRegionOptions(
        this.countryControl,
        searchTerm,
      );
    } else {
      this.filteredRegionGroups =
        this.locationFormHelper.getAllRegionGroups(searchTerm);
    }
  }

  private getRegionOptionsForCurrentMode(): Observable<ApiClient.RegionDto[]> {
    return this.countryRegionValidation
      ? this.locationFormHelper.getRegionOptions(this.countryControl)
      : this.locationFormHelper.getAllRegionOptions();
  }

  clearSelection() {
    this.countryControl.setValue(null);
  }

  clearRegionSelection() {
    this.regionControl.setValue(null);
  }

  onSearchCountry(event: string) {
    const searchTerm = event.toLowerCase();
    this.filteredCountries = this._getCountryOptions().pipe(
      takeUntil(this.onDestroy$),
      switchMap((countries) =>
        of(
          countries.filter(
            (country) =>
              country.code.toLowerCase().startsWith(searchTerm) ||
              country.name.toLowerCase().startsWith(searchTerm),
          ),
        ),
      ),
    );
  }

  // Composes both exclusion intents: the feature branch's generic/placeholder
  // (XX) filter via getCountryOptions({ excludeGeneric }) (US 7568852), and
  // develop's sanctioned-country filter.
  private _getCountryOptions(): Observable<ApiClient.ICountryDto[]> {
    return this.locationFormHelper
      .getCountryOptions(this.countryControl, {
        excludeGeneric: this.excludeGenericCountries,
      })
      .pipe(
        switchMap((countries) =>
          of(
            this.isExcludeSanctionedCountry
              ? countries.filter(
                  (country) => !(country.isSanctioned || country.isGeneric),
                )
              : countries,
          ),
        ),
      );
  }

  showCountryInformationModal() {
    this._infoPanel.open({
      title: 'FMV country',
      contentHtml: `<p>In certain locations, specific fair market values are required. Examples
    include Merchant Banker Valuation (India), 30 Day Average (Italy), Lowest
    Fair Market Value of the Day (Germany), etc. If you would like to provide a
    specific fair market such as these, add an additional fair market value and
    select the applicable country it should be used for.</p>`,
    });
  }

  showSocialTaxInformationModal() {
    this._infoPanel.open({
      title: 'Social tax',
      contentHtml: `<p>
    When this input is populated, this is the only country that will be
    considered for social tax purposes. All other data inputs (e.g. CoCs) will
    be ignored.
  </p>`,
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
